import { RentPaidFields } from '@features/contracts/components/RentPaidFields'
import {
  type FlatPaymentType,
  type OfferPaymentType,
  type RentCreateInput,
  useRentCreate
} from '@services/contracts/mutations/useRentCreate'
import { PlusCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlatDetail, OfferDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** The client's payment on an offer, or SNA's payment to a flat's landlord. */
type RentCreateModalProps =
  | { offerId: string; offer?: OfferDetail; flat?: never }
  | { flat: FlatDetail; offerId?: never; offer?: never }

type PaymentType = OfferPaymentType | FlatPaymentType

const isRentType = (type?: PaymentType) =>
  type === 'RENT' || type === 'LANDLORD_RENT'

const currentSpecificSymbol = () => {
  const now = new Date()
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`
}

// Keep YYYYMM well-formed: roll month overflow/underflow into the year.
const normalizeSpecificSymbol = (value: string) => {
  if (value.length !== 6) return value
  const year = parseInt(value.slice(0, 4), 10)
  const month = parseInt(value.slice(4, 6), 10)
  if (month > 12) return `${year + 1}01`
  if (month < 1) return `${year - 1}12`
  return value
}

export function RentCreateModal(props: RentCreateModalProps) {
  const { offerId, offer, flat } = props
  const createRent = useRentCreate()
  const { t } = useTranslation('contract')
  const [open, setOpen] = useState(false)

  const [rent, setRent] = useState('')
  const [monthly, setMonthly] = useState('')
  const [dueDate, setDueDate] = useState<string | undefined>()
  const [paymentType, setPaymentType] = useState<PaymentType | undefined>()
  const [variableSymbol, setVariableSymbol] = useState('')
  const [specificSymbol, setSpecificSymbol] = useState('')
  const [paid, setPaid] = useState(false)
  const [paidDate, setPaidDate] = useState<string | undefined>()
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<Record<string, boolean>>({})

  const typeOptions: { label: string; value: PaymentType }[] = flat
    ? [
        {
          label: t('create_modal.landlord_deposit_type'),
          value: 'LANDLORD_DEPOSIT'
        },
        { label: t('create_modal.landlord_rent_type'), value: 'LANDLORD_RENT' }
      ]
    : [
        { label: t('create_modal.rent_type'), value: 'RENT' },
        { label: t('create_modal.deposit_type'), value: 'DEPOSIT' }
      ]

  // Amounts the payment usually has, filled in when its type is picked.
  const defaults = (type: PaymentType) => {
    switch (type) {
      case 'RENT':
        return { rent: offer?.rent, monthly: offer?.monthly_payments }
      case 'DEPOSIT':
        return { rent: offer?.deposit_amount, monthly: undefined }
      case 'LANDLORD_RENT':
        return { rent: flat?.rent, monthly: undefined }
      case 'LANDLORD_DEPOSIT':
        return { rent: flat?.deposit, monthly: undefined }
    }
  }

  const selectType = (type: PaymentType) => {
    const d = defaults(type)
    setPaymentType(type)
    setRent(d.rent != null ? String(d.rent) : '')
    setMonthly(d.monthly != null ? String(d.monthly) : '')
    setSpecificSymbol(isRentType(type) ? currentSpecificSymbol() : '')
  }

  const prefill = () => {
    selectType(flat ? 'LANDLORD_DEPOSIT' : 'RENT')
    setDueDate(undefined)
    setVariableSymbol('')
    setPaid(false)
    setPaidDate(undefined)
    setDescription('')
    setErrors({})
  }

  const handleSubmit = () => {
    const rentType = isRentType(paymentType)
    const nextErrors = {
      rent: rent === '' || Number(rent) <= 0,
      due_date: !dueDate,
      payment_type: !paymentType,
      specific_symbol: rentType && specificSymbol.length !== 6
    }
    setErrors(nextErrors)
    if (Object.values(nextErrors).some(Boolean)) return

    const common = {
      rent: Number(rent),
      monthly_advance_prescription: rentType ? Number(monthly || 0) : 0,
      due_date: dueDate as string,
      variable_symbol: variableSymbol || undefined,
      specific_symbol: rentType ? specificSymbol : '',
      description,
      paid,
      paid_date: paid ? (paidDate ?? null) : null
    }
    const data: RentCreateInput = flat
      ? {
          ...common,
          flat: flat.id as string,
          payment_type: paymentType as FlatPaymentType
        }
      : {
          ...common,
          offer: offerId as string,
          payment_type: paymentType as OfferPaymentType
        }
    createRent.mutate(data, { onSuccess: () => setOpen(false) })
  }

  const rentType = isRentType(paymentType)

  return (
    <>
      <Button
        onClick={() => {
          prefill()
          setOpen(true)
        }}
      >
        <PlusCircle className="h-4 w-4" />
        {t('create_modal.create_payment')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <PlusCircle className="h-5 w-5" />
              </span>
              <DialogTitle>{t('create_modal.create_payment')}</DialogTitle>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label={t('create_modal.payment_type')}
              required
              error={errors.payment_type}
            >
              <Combobox
                value={paymentType}
                onChange={(v) => v && selectType(v as PaymentType)}
                searchable={false}
                options={typeOptions}
                invalid={errors.payment_type}
              />
            </Field>
            <Field
              label={t('create_modal.due_date')}
              required
              error={errors.due_date}
            >
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                placeholder={t('create_modal.select_date')}
                invalid={errors.due_date}
              />
            </Field>
            <Field
              label={t(rentType ? 'create_modal.rent' : 'create_modal.amount')}
              required
              error={errors.rent}
            >
              <Amount value={rent} onChange={setRent} />
            </Field>
            {rentType && (
              <Field label={t('create_modal.monthly_payments')}>
                <Amount value={monthly} onChange={setMonthly} />
              </Field>
            )}
            <Field label={t('create_modal.variable_symbol')}>
              <Input
                inputMode="numeric"
                maxLength={10}
                value={variableSymbol}
                placeholder={t('create_modal.auto_generated')}
                onChange={(e) =>
                  setVariableSymbol(e.target.value.replace(/\D/g, ''))
                }
              />
            </Field>
            {rentType && (
              <Field
                label={t('create_modal.specific_symbol')}
                required
                error={errors.specific_symbol}
              >
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={specificSymbol}
                  placeholder="YYYYMM"
                  onChange={(e) =>
                    setSpecificSymbol(
                      normalizeSpecificSymbol(e.target.value.replace(/\D/g, ''))
                    )
                  }
                />
              </Field>
            )}
          </div>

          <RentPaidFields
            paid={paid}
            onPaidChange={setPaid}
            paidDate={paidDate}
            onPaidDateChange={setPaidDate}
            description={description}
            onDescriptionChange={setDescription}
            descriptionPlaceholder={t('create_modal.description_placeholder')}
            clearDateWhenUnpaid
          />

          <DialogFooter>
            <Button variant="ghostDark" onClick={() => setOpen(false)}>
              {t('create_modal.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={createRent.isPending}>
              {t('create_modal.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Field({
  label,
  required,
  error,
  children
}: {
  label: string
  required?: boolean
  error?: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation('contract')
  return (
    <div>
      <Label className="mb-1.5 block">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1.5 text-sm font-medium text-destructive">
          {t('create_modal.required')}
        </p>
      )}
    </div>
  )
}

function Amount({
  value,
  onChange
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="relative">
      <Input
        type="number"
        min={0}
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pr-9"
      />
      <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
        €
      </span>
    </div>
  )
}
